import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";

export function LightColumn() {
  const [beamWidth, setBeamWidth] = useState(35);
  const [glowPulse, setGlowPulse] = useState(0);
  const [userControlled, setUserControlled] = useState(false);

  // Subtle beam animation (pauses when user takes control)
  useEffect(() => {
    if (userControlled) return;
    const interval = setInterval(() => {
      setBeamWidth((w) => {
        const next = w + (Math.random() > 0.5 ? 2 : -2);
        return Math.max(15, Math.min(70, next));
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [userControlled]);

  // Glow pulse for Flos
  useEffect(() => {
    const interval = setInterval(() => {
      setGlowPulse((p) => (p + 1) % 100);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const glowIntensity = 0.4 + Math.sin(glowPulse * 0.06) * 0.15;

  return (
    <div className="flex flex-col">
      <p className="font-sans text-[10px] font-medium uppercase tracking-[0.3em] text-[hsl(0,0%,40%)]">
        Light
      </p>

      {/* ERCO — Spotlight simulation */}
      <div className="mt-8">
        <h3 className="font-sans text-lg font-light tracking-wide text-[hsl(0,0%,92%)]">
          ERCO
        </h3>
        <p className="mt-1 font-sans text-xs text-[hsl(0,0%,50%)]">
          Precision Beam Control
        </p>
        <p className="mt-2 font-sans text-[10px] uppercase tracking-[0.25em] text-[hsl(0,0%,38%)]">
          Drag to adjust beam angle
        </p>

        <div className="relative mt-4 h-40 overflow-hidden rounded-lg border border-[hsl(0,0%,12%)] bg-[hsl(0,0%,4%)]">
          {/* Wall with painting placeholder */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="relative h-20 w-14 border border-[hsl(0,0%,15%)]"
              style={{
                boxShadow: `0 0 ${beamWidth}px hsl(45, 15%, ${12 + beamWidth * 0.3}%), 
                             0 0 ${beamWidth * 2}px hsl(45, 10%, ${8 + beamWidth * 0.15}%)`,
              }}
            >
              <div className="absolute inset-[2px] overflow-hidden">
                <div
                  className="h-full w-full transition-all duration-500"
                  style={{
                    background: `linear-gradient(170deg,
                      hsl(35, 20%, ${10 + beamWidth * 0.4}%) 0%,
                      hsl(25, 15%, ${8 + beamWidth * 0.3}%) 40%,
                      hsl(30, 18%, ${12 + beamWidth * 0.35}%) 100%)`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Spotlight beam cone */}
          <div
            className="absolute left-1/2 top-0 -translate-x-1/2 transition-all duration-500"
            style={{
              width: 0,
              height: 0,
              borderLeft: `${beamWidth}px solid transparent`,
              borderRight: `${beamWidth}px solid transparent`,
              borderTop: `140px solid hsl(45, 12%, 12%)`,
              opacity: 0.25,
              filter: `blur(${beamWidth * 0.3}px)`,
            }}
          />

          {/* Source point */}
          <div
            className="absolute left-1/2 top-2 h-1 w-1 -translate-x-1/2 rounded-full"
            style={{
              backgroundColor: "hsl(45, 30%, 60%)",
              boxShadow: "0 0 8px hsl(45, 25%, 45%)",
            }}
          />
        </div>

        {/* Interactive beam angle slider */}
        <div className="mt-4 flex items-center gap-4">
          <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[hsl(0,0%,40%)]">Narrow</span>
          <Slider
            value={[beamWidth]}
            min={10}
            max={70}
            step={1}
            onValueChange={(v) => {
              setUserControlled(true);
              setBeamWidth(v[0]);
            }}
            className="flex-1 [&_[data-slot=range]]:bg-[hsl(45,15%,30%)] [&_[data-slot=track]]:bg-[hsl(0,0%,12%)] [&_[data-slot=track]]:h-1 [&_[data-slot=thumb]]:h-3 [&_[data-slot=thumb]]:w-3 [&_[data-slot=thumb]]:border-[hsl(45,20%,40%)] [&_[data-slot=thumb]]:bg-[hsl(45,15%,25%)]"
          />
          <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[hsl(0,0%,40%)]">Wide</span>
        </div>
        <div className="mt-1 text-center font-sans text-[10px] tabular-nums text-[hsl(0,0%,35%)]">
          {Math.round(beamWidth * 1.5)}° beam angle
        </div>
      </div>

      {/* Viabizzuno — Linear light */}
      <div className="mt-10">
        <h3 className="font-sans text-sm font-light text-[hsl(0,0%,88%)]">
          Viabizzuno
        </h3>
        <p className="mt-0.5 font-sans text-[11px] text-[hsl(0,0%,45%)]">
          Architectural Light Language
        </p>

        <div className="relative mt-4 h-20 overflow-hidden rounded-lg border border-[hsl(0,0%,12%)] bg-[hsl(0,0%,4%)]">
          {/* Architectural wall lines */}
          <div className="absolute inset-0 flex flex-col justify-center gap-8 px-6">
            {[0, 1].map((i) => (
              <div key={i} className="relative">
                <div
                  className="h-px w-full"
                  style={{
                    background: `linear-gradient(90deg, 
                      transparent 0%, 
                      hsl(40, 15%, 35%) 20%, 
                      hsl(40, 20%, 45%) 50%, 
                      hsl(40, 15%, 35%) 80%, 
                      transparent 100%)`,
                  }}
                />
                <div
                  className="absolute -top-2 left-0 h-4 w-full"
                  style={{
                    background: `linear-gradient(90deg, 
                      transparent 0%, 
                      hsl(40, 10%, 15%) 20%, 
                      hsl(40, 12%, 20%) 50%, 
                      hsl(40, 10%, 15%) 80%, 
                      transparent 100%)`,
                    filter: "blur(4px)",
                    opacity: 0.6,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Flos + iGuzzini */}
      <div className="mt-10 grid grid-cols-2 gap-4">
        {/* Flos — Sculptural glow */}
        <div>
          <div className="relative h-28 overflow-hidden rounded-lg border border-[hsl(0,0%,12%)] bg-[hsl(0,0%,4%)]">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Sculptural orb */}
              <div
                className="h-10 w-10 rounded-full transition-all duration-1000"
                style={{
                  background: `radial-gradient(circle, 
                    hsl(35, 20%, ${18 + glowIntensity * 20}%) 0%, 
                    hsl(30, 15%, ${10 + glowIntensity * 10}%) 40%, 
                    transparent 70%)`,
                  boxShadow: `0 0 ${30 * glowIntensity}px hsl(35, 20%, ${15 + glowIntensity * 15}%), 
                              0 0 ${60 * glowIntensity}px hsl(35, 15%, ${8 + glowIntensity * 8}%)`,
                }}
              />
            </div>
          </div>
          <h3 className="mt-3 font-sans text-xs font-light text-[hsl(0,0%,85%)]">Flos</h3>
          <p className="mt-0.5 font-sans text-[10px] text-[hsl(0,0%,42%)]">Decorative Expression</p>
        </div>

        {/* iGuzzini — Technical beam */}
        <div>
          <div className="relative h-28 overflow-hidden rounded-lg border border-[hsl(0,0%,12%)] bg-[hsl(0,0%,4%)]">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Clean downlight beam */}
              <div className="relative">
                <div
                  className="h-16 w-px"
                  style={{
                    background: `linear-gradient(to bottom, 
                      hsl(210, 10%, 55%) 0%, 
                      hsl(210, 8%, 35%) 60%, 
                      transparent 100%)`,
                  }}
                />
                {/* Pool of light at bottom */}
                <div
                  className="absolute -bottom-1 left-1/2 h-2 w-8 -translate-x-1/2 rounded-full"
                  style={{
                    background: `radial-gradient(ellipse, hsl(210, 8%, 25%) 0%, transparent 70%)`,
                    filter: "blur(3px)",
                  }}
                />
                {/* Source */}
                <div
                  className="absolute -top-1 left-1/2 h-1.5 w-3 -translate-x-1/2 rounded-sm"
                  style={{ backgroundColor: "hsl(0, 0%, 20%)" }}
                />
              </div>
            </div>
          </div>
          <h3 className="mt-3 font-sans text-xs font-light text-[hsl(0,0%,85%)]">iGuzzini</h3>
          <p className="mt-0.5 font-sans text-[10px] text-[hsl(0,0%,42%)]">Technical Balance</p>
        </div>
      </div>
    </div>
  );
}
